import React, { useEffect, useRef } from "react";
import { FaUniversity, FaUserTie, FaGraduationCap, FaMapMarkerAlt, FaCalendarAlt } from "react-icons/fa";

const UniversitySection = () => {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10" ref={sectionRef}>
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-green-600 via-blue-500 to-green-600 bg-clip-text text-transparent animate-gradient">
              University
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Affiliated to one of India's premier technical universities
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div
            ref={(el) => (cardRefs.current[0] = el)}
            className="group opacity-0 transition-all duration-700 transform translate-y-8 hover:-translate-y-2 hover:shadow-2xl rounded-2xl shadow-lg overflow-hidden border-t-4 border-green-500 bg-gradient-to-br from-green-50 via-white to-emerald-50"
          >
            <div className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-600 to-emerald-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <FaUniversity className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">AKTU</h3>
              </div>
              <p className="text-gray-700 font-medium mb-2">Dr. A.P.J. Abdul Kalam Technical University</p>
              <p className="text-gray-600 text-sm mb-4">Lucknow, Uttar Pradesh</p>
              <div className="space-y-3 text-gray-600">
                <div className="flex items-start gap-3">
                  <FaMapMarkerAlt className="text-green-500 mt-1" />
                  <span>Sitapur Road, Lucknow - 226021, Uttar Pradesh, India</span>
                </div>
                <div className="flex items-start gap-3">
                  <FaCalendarAlt className="text-green-500 mt-1" />
                  <span>Established: 2000 (as Uttar Pradesh Technical University)</span>
                </div>
                <div className="flex items-start gap-3">
                  <FaGraduationCap className="text-green-500 mt-1" />
                  <span>Affiliation: UGC, AICTE, PCI (Pharmacy)</span>
                </div>
              </div>
            </div>
          </div>
          <div
            ref={(el) => (cardRefs.current[1] = el)}
            className="group opacity-0 transition-all duration-700 transform translate-y-8 hover:-translate-y-2 hover:shadow-2xl rounded-2xl shadow-lg overflow-hidden border-t-4 border-blue-500 bg-gradient-to-br from-blue-50 via-white to-sky-50"
          >
            <div className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-600 to-sky-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <FaUserTie className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Vice Chancellor</h3>
              </div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-sky-100 flex items-center justify-center shadow-md">
                  <FaUserTie className="text-4xl text-blue-500" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-gray-800">Prof. Vinay Kumar Pathak</p>
                  <p className="text-blue-600 text-sm">Vice Chancellor, APJAKTU, Lucknow</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mt-4">
                Professor Vinay Kumar Pathak is a distinguished academician and administrator. Under his leadership, 
                AKTU has seen remarkable growth in research, innovation, and industry-academia partnerships. 
                He is committed to fostering a culture of excellence and entrepreneurship among students.
              </p>
              <div className="mt-6 text-blue-600 text-sm flex items-center gap-2">
                <FaGraduationCap />
                <span>Leading technical education in Uttar Pradesh</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 text-center">
          <div className="inline-block bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-sm border border-gray-100">
            <p className="text-gray-600 text-sm">
              <span className="font-semibold text-green-600">Recognized by UGC, AICTE, PCI</span> • 
              <span className="font-semibold text-blue-600"> Ranked among top technical universities in India</span>
            </p>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </section>
  );
};

export default UniversitySection;